/**
 * MUI theme builder.
 *
 * This file intentionally does NOT make design decisions of its own.
 * It pulls everything from `theme/designSystem.ts` (the single source of
 * truth) and threads those tokens through MUI's component override
 * system so most pages get visually upgraded for free.
 *
 * If you want to change *how the app looks*, edit `designSystem.ts`.
 */

import { createTheme, ThemeOptions, alpha } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { TenantBranding } from '../hooks/useBranding';
import {
  buildTokens,
  typography as typeRamp,
  radius,
  motion,
  sizes,
  type Mode,
} from './designSystem';
// `muiAugmentation.d.ts` is picked up automatically via tsconfig — no runtime import.

export type ColorMode = Mode;

export function buildTheme(mode: ColorMode, branding?: TenantBranding | null) {
  const tokens = buildTokens(mode, branding);

  const base: ThemeOptions = {
    palette: {
      mode,
      primary: { main: tokens.brand.primary, light: tokens.brand.primarySoft, dark: tokens.brand.primaryHover, contrastText: tokens.brand.onPrimary } as any,
      secondary: { main: tokens.brand.primary, light: tokens.brand.primarySoft, dark: tokens.brand.primaryHover, contrastText: tokens.brand.onPrimary } as any,
      success: { main: tokens.semantic.success, light: tokens.semantic.successSoft as any, contrastText: '#FFFFFF' } as any,
      warning: { main: tokens.semantic.warning, light: tokens.semantic.warningSoft as any, contrastText: '#FFFFFF' } as any,
      error:   { main: tokens.semantic.danger,  light: tokens.semantic.dangerSoft as any,  contrastText: '#FFFFFF' } as any,
      info:    { main: tokens.semantic.info,    light: tokens.semantic.infoSoft as any,    contrastText: '#FFFFFF' } as any,
      divider: tokens.border.subtle,
      text: {
        primary: tokens.text.primary,
        secondary: tokens.text.secondary,
        disabled: tokens.text.disabled,
      },
      background: {
        default: tokens.surface.canvas,
        paper: tokens.surface.raised,
      },
      action: {
        hover: alpha(tokens.text.primary, mode === 'light' ? 0.04 : 0.06),
        selected: tokens.brand.primarySoft,
        disabled: tokens.text.disabled,
        disabledBackground: alpha(tokens.text.primary, mode === 'light' ? 0.06 : 0.08),
      },
    },
    shape: { borderRadius: radius.md },
    typography: {
      fontFamily: typeRamp.fontFamily,
      fontFamilyMono: typeRamp.fontFamilyMono,
      htmlFontSize: typeRamp.htmlFontSize,
      h1: typeRamp.h1,
      h2: typeRamp.h2,
      h3: typeRamp.h3,
      h4: typeRamp.h4,
      h5: typeRamp.h5,
      h6: typeRamp.h6,
      subtitle1: typeRamp.subtitle1,
      subtitle2: typeRamp.subtitle2,
      body1: typeRamp.body1,
      body2: typeRamp.body2,
      button: typeRamp.button,
      caption: typeRamp.caption,
      overline: typeRamp.overline,
      // Custom variants
      displayLg: typeRamp.displayLg,
      display: typeRamp.display,
      displaySm: typeRamp.displaySm,
      bodyLg: typeRamp.bodyLg,
      eyebrow: typeRamp.eyebrow,
    },
    transitions: {
      duration: {
        shortest: motion.duration.instant,
        shorter: motion.duration.fast,
        short: motion.duration.normal,
        standard: motion.duration.normal,
        complex: motion.duration.slow,
        enteringScreen: motion.duration.normal,
        leavingScreen: motion.duration.fast,
      },
      easing: {
        easeInOut: motion.easing.standard,
        easeOut: motion.easing.enter,
        easeIn: motion.easing.exit,
        sharp: motion.easing.standard,
      },
    },
    components: {
      // ── Baseline
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            WebkitTextSizeAdjust: '100%',
            textSizeAdjust: '100%',
          },
          body: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            backgroundColor: tokens.surface.canvas,
            color: tokens.text.primary,
          },
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-track': { background: 'transparent' },
          '*::-webkit-scrollbar-thumb': {
            background: tokens.border.default,
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: tokens.border.strong,
            backgroundClip: 'padding-box',
          },
          // Use a neutral tint (text.primary) rather than the brand color so
          // selection stays visible on brand-tinted surfaces too — e.g. the
          // user-message bubble in Copilot is brand primary, and a brand
          // primary selection on top of that was nearly invisible. Higher
          // alpha than before so the highlight reads on raised/sunken cards
          // as well. We deliberately don't force a `color` so text keeps its
          // native contrast (white text on user bubble stays white, etc.).
          '::selection': {
            background: alpha(tokens.text.primary, mode === 'light' ? 0.20 : 0.32),
          },
        },
      },

      // ── Surfaces
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${tokens.border.subtle}`,
            boxShadow: tokens.shadow.xs,
          },
          outlined: {
            borderColor: tokens.border.default,
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            border: `1px solid ${tokens.border.subtle}`,
            boxShadow: tokens.shadow.sm,
            backgroundImage: 'none',
          },
        },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'default' },
        styleOverrides: {
          root: {
            backgroundColor: tokens.surface.raised,
            color: tokens.text.primary,
            backgroundImage: 'none',
            boxShadow: 'none',
            borderBottom: `1px solid ${tokens.border.subtle}`,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: `${sizes.header}px !important`,
          },
        },
      },

      // ── Buttons
      MuiButton: {
        defaultProps: { size: 'medium', disableElevation: true, disableRipple: false },
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            fontWeight: 600,
            textTransform: 'none',
            letterSpacing: 0.1,
            transition: `background-color ${motion.duration.fast}ms ${motion.easing.standard}, border-color ${motion.duration.fast}ms ${motion.easing.standard}, color ${motion.duration.fast}ms ${motion.easing.standard}, box-shadow ${motion.duration.fast}ms ${motion.easing.standard}`,
          },
          sizeSmall: { paddingTop: 4, paddingBottom: 4, paddingLeft: 10, paddingRight: 10, fontSize: typeRamp.buttonSm.fontSize },
          sizeMedium: { paddingTop: 6, paddingBottom: 6, paddingLeft: 14, paddingRight: 14 },
          sizeLarge: { paddingTop: 9, paddingBottom: 9, paddingLeft: 18, paddingRight: 18 },
          contained: {
            boxShadow: tokens.shadow.none,
            '&:hover': { boxShadow: tokens.shadow.none, backgroundColor: tokens.brand.primaryHover },
            '&:active': { backgroundColor: tokens.brand.primaryPressed },
            '&.Mui-focusVisible': { boxShadow: tokens.shadow.focusRing },
          },
          outlined: {
            borderColor: tokens.border.default,
            color: tokens.text.primary,
            '&:hover': {
              borderColor: tokens.border.strong,
              backgroundColor: alpha(tokens.text.primary, mode === 'light' ? 0.04 : 0.06),
            },
            '&.Mui-focusVisible': { boxShadow: tokens.shadow.focusRing, borderColor: tokens.brand.primary },
          },
          text: {
            color: tokens.text.primary,
            '&:hover': { backgroundColor: alpha(tokens.text.primary, mode === 'light' ? 0.04 : 0.06) },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            color: tokens.text.secondary,
            transition: `background-color ${motion.duration.fast}ms ${motion.easing.standard}, color ${motion.duration.fast}ms ${motion.easing.standard}`,
            '&:hover': {
              color: tokens.text.primary,
              backgroundColor: alpha(tokens.text.primary, mode === 'light' ? 0.05 : 0.08),
            },
            '&.Mui-focusVisible': { boxShadow: tokens.shadow.focusRing },
          },
          sizeSmall: { padding: 6 },
        },
      },

      // ── Inputs
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            backgroundColor: tokens.surface.raised,
            transition: `border-color ${motion.duration.fast}ms ${motion.easing.standard}, box-shadow ${motion.duration.fast}ms ${motion.easing.standard}`,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.border.strong,
            },
            '&.Mui-focused': {
              boxShadow: tokens.shadow.focusRing,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.brand.primary,
              borderWidth: 1,
            },
          },
          notchedOutline: {
            borderColor: tokens.border.default,
            transition: `border-color ${motion.duration.fast}ms ${motion.easing.standard}`,
          },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small' },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            // iOS Safari zooms when input < 16px on touch devices.
            '@media (hover: none) and (pointer: coarse)': { fontSize: '1rem' },
          },
          input: {
            '@media (hover: none) and (pointer: coarse)': { fontSize: '1rem' },
            '&::placeholder': { color: tokens.text.tertiary, opacity: 1 },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: { color: tokens.text.tertiary },
        },
      },

      // ── Surfaces (popovers, menus, dialogs, drawers)
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: radius.md,
            border: `1px solid ${tokens.border.subtle}`,
            boxShadow: tokens.shadow.lg,
            marginTop: 6,
            backgroundImage: 'none',
          },
          list: { padding: 6 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: radius.xs,
            minHeight: 36,
            paddingLeft: 10,
            paddingRight: 10,
            fontSize: typeRamp.body2.fontSize,
            color: tokens.text.primary,
            '&:hover': {
              backgroundColor: alpha(tokens.text.primary, mode === 'light' ? 0.04 : 0.06),
            },
            '&.Mui-selected': {
              backgroundColor: tokens.brand.primarySoft,
              color: tokens.text.accent,
              '&:hover': { backgroundColor: tokens.brand.primarySoft },
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: radius.md,
            border: `1px solid ${tokens.border.subtle}`,
            boxShadow: tokens.shadow.lg,
            backgroundImage: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: radius.lg,
            border: `1px solid ${tokens.border.subtle}`,
            boxShadow: tokens.shadow.xl,
            backgroundImage: 'none',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            fontSize: typeRamp.h4.fontSize,
            paddingTop: 18,
            paddingBottom: 12,
            paddingLeft: 22,
            paddingRight: 22,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { paddingLeft: 22, paddingRight: 22 },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: { padding: 14, paddingTop: 8, gap: 6 },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            borderColor: tokens.border.subtle,
          },
        },
      },

      // ── Tooltips
      MuiTooltip: {
        defaultProps: { arrow: false, enterDelay: 200, leaveDelay: 0 },
        styleOverrides: {
          tooltip: {
            backgroundColor: tokens.surface.inverse,
            color: tokens.text.inverse,
            borderRadius: radius.sm,
            padding: '6px 10px',
            fontSize: typeRamp.caption.fontSize,
            fontWeight: 500,
            boxShadow: tokens.shadow.md,
          },
          arrow: { color: tokens.surface.inverse },
        },
      },

      // ── Chips, badges, alerts
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            fontWeight: 500,
            fontSize: typeRamp.caption.fontSize,
            height: 24,
          },
          sizeSmall: { height: 22, fontSize: typeRamp.caption.fontSize },
          // Filled-variant rules. We can't use the per-color slots
          // (`filledSuccess` / `filledError` / etc.) because MUI's typed
          // override map doesn't expose them — so we scope each colour with
          // a class selector inside `filled`. Without these scopes a single
          // unscoped `filled` rule would wash out semantic colors (the bug
          // we're fixing — Active / Profiled / Failed badges going grey).
          filled: {
            '&.MuiChip-colorDefault': {
              backgroundColor: alpha(tokens.text.primary, mode === 'light' ? 0.05 : 0.08),
              color: tokens.text.primary,
            },
            '&.MuiChip-colorPrimary': {
              backgroundColor: tokens.brand.primarySoft,
              color: tokens.brand.primary,
            },
            '&.MuiChip-colorSuccess': {
              backgroundColor: tokens.semantic.successSoft,
              color: tokens.semantic.success,
            },
            '&.MuiChip-colorError': {
              backgroundColor: tokens.semantic.dangerSoft,
              color: tokens.semantic.danger,
            },
            '&.MuiChip-colorWarning': {
              backgroundColor: tokens.semantic.warningSoft,
              color: tokens.semantic.warning,
            },
            '&.MuiChip-colorInfo': {
              backgroundColor: tokens.semantic.infoSoft,
              color: tokens.semantic.info,
            },
            // Leading icons inherit the chip's semantic color so e.g. the
            // checkmark in "Available" picks up the success green.
            '& .MuiChip-icon': { color: 'inherit' },
          },
          // Outlined-variant rules — tint border + text per semantic color.
          outlined: {
            borderColor: tokens.border.default,
            '&.MuiChip-colorPrimary': {
              borderColor: alpha(tokens.brand.primary, 0.4),
              color: tokens.brand.primary,
            },
            '&.MuiChip-colorSuccess': {
              borderColor: alpha(tokens.semantic.success, 0.4),
              color: tokens.semantic.success,
            },
            '&.MuiChip-colorError': {
              borderColor: alpha(tokens.semantic.danger, 0.4),
              color: tokens.semantic.danger,
            },
            '&.MuiChip-colorWarning': {
              borderColor: alpha(tokens.semantic.warning, 0.45),
              color: tokens.semantic.warning,
            },
            '&.MuiChip-colorInfo': {
              borderColor: alpha(tokens.semantic.info, 0.4),
              color: tokens.semantic.info,
            },
          },
          clickable: {
            transition: `background-color ${motion.duration.fast}ms ${motion.easing.standard}, border-color ${motion.duration.fast}ms ${motion.easing.standard}`,
            '&:hover': { backgroundColor: alpha(tokens.brand.primary, 0.08) },
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: { fontWeight: 600, fontSize: '0.6875rem' },
        },
      },
      MuiAlert: {
        defaultProps: { variant: 'standard' },
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            border: `1px solid transparent`,
            padding: '8px 12px',
            fontSize: typeRamp.body2.fontSize,

            // Make close / action buttons inherit the alert's foreground
            // colour. Without this the global MuiIconButton override
            // (which forces text.secondary) makes the close "X" invisible
            // on filled alerts and snackbars (e.g. on the brand-purple
            // info background). Inheriting + a translucent hover surface
            // works for both standard (soft) and filled (saturated) skins.
            '& .MuiAlert-action': {
              alignItems: 'center',
              '& .MuiIconButton-root': {
                color: 'inherit',
                opacity: 0.85,
                '&:hover': {
                  color: 'inherit',
                  opacity: 1,
                  backgroundColor: alpha('#fff', 0.16),
                },
                '&.Mui-focusVisible': { boxShadow: tokens.shadow.focusRing },
              },
              '& .MuiButton-root': { color: 'inherit' },
            },

            // Severity icons should match foreground for visual unity
            // across both standard and filled variants.
            '& .MuiAlert-icon': { color: 'inherit', opacity: 0.95 },
          },
          standardError: { backgroundColor: tokens.semantic.dangerSoft, color: tokens.semantic.danger, borderColor: alpha(tokens.semantic.danger, 0.24) },
          standardWarning: { backgroundColor: tokens.semantic.warningSoft, color: tokens.semantic.warning, borderColor: alpha(tokens.semantic.warning, 0.28) },
          standardSuccess: { backgroundColor: tokens.semantic.successSoft, color: tokens.semantic.success, borderColor: alpha(tokens.semantic.success, 0.24) },
          standardInfo: { backgroundColor: tokens.semantic.infoSoft, color: tokens.semantic.info, borderColor: alpha(tokens.semantic.info, 0.24) },

          // Filled variants — saturated background with white foreground.
          // Pinned to our semantic tokens so brand colour stays the source
          // of truth (info uses brand primary by design).
          filledError: { backgroundColor: tokens.semantic.danger, color: '#fff' },
          filledWarning: { backgroundColor: tokens.semantic.warning, color: '#fff' },
          filledSuccess: { backgroundColor: tokens.semantic.success, color: '#fff' },
          filledInfo: { backgroundColor: tokens.semantic.info, color: '#fff' },

          // Outlined variants — coloured border + transparent background,
          // foreground matches severity. Action buttons inherit cleanly.
          outlinedError: { color: tokens.semantic.danger, borderColor: alpha(tokens.semantic.danger, 0.5) },
          outlinedWarning: { color: tokens.semantic.warning, borderColor: alpha(tokens.semantic.warning, 0.5) },
          outlinedSuccess: { color: tokens.semantic.success, borderColor: alpha(tokens.semantic.success, 0.5) },
          outlinedInfo: { color: tokens.semantic.info, borderColor: alpha(tokens.semantic.info, 0.5) },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            backgroundColor: tokens.surface.inverse,
            color: tokens.text.inverse,
            boxShadow: tokens.shadow.lg,
          },
        },
      },

      // ── Tabs
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 36,
            borderBottom: `1px solid ${tokens.border.subtle}`,
          },
          indicator: {
            height: 2,
            borderRadius: 2,
            backgroundColor: tokens.brand.primary,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 36,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: typeRamp.body2.fontSize,
            color: tokens.text.secondary,
            paddingLeft: 12,
            paddingRight: 12,
            '&.Mui-selected': { color: tokens.text.primary },
            '&:hover': { color: tokens.text.primary },
          },
        },
      },

      // ── Lists
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            paddingTop: 6,
            paddingBottom: 6,
            transition: `background-color ${motion.duration.fast}ms ${motion.easing.standard}, color ${motion.duration.fast}ms ${motion.easing.standard}`,
            '&:hover': { backgroundColor: alpha(tokens.text.primary, mode === 'light' ? 0.04 : 0.06) },
            '&.Mui-selected': {
              backgroundColor: tokens.brand.primarySoft,
              color: tokens.text.accent,
              '&:hover': { backgroundColor: tokens.brand.primarySoft },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: 'inherit',
            minWidth: 32,
          },
        },
      },

      // ── Dividers
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: tokens.border.subtle,
          },
        },
      },

      // ── Tables
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: tokens.border.subtle,
            fontSize: typeRamp.body2.fontSize,
          },
          head: {
            fontWeight: 600,
            color: tokens.text.secondary,
            backgroundColor: tokens.surface.sunken,
            fontSize: typeRamp.caption.fontSize,
            letterSpacing: 0.3,
            textTransform: 'uppercase',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: `background-color ${motion.duration.fast}ms ${motion.easing.standard}`,
            '&:hover': {
              backgroundColor: alpha(tokens.text.primary, mode === 'light' ? 0.025 : 0.04),
            },
          },
        },
      },

      // ── Skeleton
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(tokens.text.primary, mode === 'light' ? 0.06 : 0.10),
          },
          rounded: { borderRadius: radius.sm },
        },
      },

      // ── Accordion
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            border: `1px solid ${tokens.border.subtle}`,
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
          },
        },
      },

      // ── LinearProgress (subtler in light mode)
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: radius.full,
            backgroundColor: alpha(tokens.text.primary, mode === 'light' ? 0.06 : 0.10),
          },
          bar: { borderRadius: radius.full, backgroundColor: tokens.brand.primary },
        },
      },

      // ── Switch / Checkbox / Radio refinements
      MuiSwitch: {
        styleOverrides: {
          root: {
            padding: 8,
          },
          switchBase: {
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: tokens.brand.primary,
              opacity: 0.5,
            },
            '&.Mui-checked .MuiSwitch-thumb': {
              backgroundColor: tokens.brand.primary,
            },
          },
        },
      },
    },
  };

  // Carry tokens onto the theme so consumers can read them directly:
  //   sx={(theme) => ({ borderColor: theme.tokens.border.subtle })}
  const augmented: ThemeOptions = {
    ...base,
    tokens,
    ds: { radius, motion, sizes },
  } as ThemeOptions;

  return createTheme(deepmerge(augmented, {}));
}
